import { Alert } from '@zorro/design-system/v2/Alert';
import { Checkbox } from '@zorro/design-system/v2/Checkbox';
import { Box, Stack, Text } from '@zorro/web/design-system';
import { useMonolithQuery } from '@zorro/web/ui-utils';
import { Controller, UseFormReturn } from 'react-hook-form';

import { ProofOfConsentSection } from '../../../ProofOfConsent';
import { CarrierApplicationSubmitFormFields } from '../CarrierCompleteStepUtils';

type Props = {
  employeeId: string;
  onboardingPeriodId: string;
  isVisible: boolean;
  isAccepted: boolean;
  brokerAttestation?: string;
  electionSubmittedNotice?: string;
  submissionForm: UseFormReturn<CarrierApplicationSubmitFormFields>;
};

export const CarrierBrokerAttestationSection = ({
  employeeId,
  onboardingPeriodId,
  isVisible,
  isAccepted,
  brokerAttestation,
  electionSubmittedNotice,
  submissionForm,
}: Props) => {
  // The form seeds proofOfConsentDocuments from this query, so the seeding
  // reset would discard a file picked before it resolves.
  const { isLoading: isLoadingOnboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });

  if (!isVisible || !brokerAttestation) return null;

  return (
    <>
      <Stack gap="md" mt="2rem">
        <Text size="md" fw={600}>
          Attestation required by HealthSherpa
        </Text>
        <Controller
          control={submissionForm.control}
          name="brokerAttestationAccepted"
          render={({ field: { value, ...rest } }) => (
            <Checkbox
              {...rest}
              checked={Boolean(value)}
              label={brokerAttestation}
            />
          )}
        />
        {electionSubmittedNotice && (
          <Alert variant="intro" mt="xs">
            {electionSubmittedNotice}
          </Alert>
        )}
      </Stack>
      {isAccepted && !isLoadingOnboardingPeriod && (
        <Box mt="xl">
          <Controller
            control={submissionForm.control}
            name="proofOfConsentDocuments"
            render={({ field: { value, onChange } }) => (
              <ProofOfConsentSection
                onboardingPeriodId={onboardingPeriodId}
                documents={Array.isArray(value) ? value : []}
                subtitle="Upload the employee's written consent to submit this application on their behalf."
                dropzoneTitle="Upload proof of consent"
                onChange={onChange}
              />
            )}
          />
        </Box>
      )}
    </>
  );
};
